import React, { useState } from 'react';
import { CheckCircle2, ExternalLink, ShieldCheck, TrendingDown, Copy } from 'lucide-react';
import { Button } from '@/components/Button';
import { openLink } from '@/utils';

interface GeneratedResultProps {
    result: string;
    isMobile: boolean;
}

export const GeneratedResult: React.FC<GeneratedResultProps> = ({ result, isMobile }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(result);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="mt-6 relative overflow-hidden animate-in fade-in slide-in-from-bottom-6 duration-700">
            {/* Background glow for success */}
            <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 to-blue-500/10 rounded-2xl blur-xl"></div>

            <div className="relative bg-white dark:bg-gray-800 border border-green-500/30 rounded-2xl p-6 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <div className="p-1 bg-green-500 text-white rounded-full">
                            <CheckCircle2 className="w-4 h-4" />
                        </div>
                        <h3 className="font-bold text-gray-900 dark:text-white text-sm uppercase tracking-wide">
                            Melhor condição encontrada
                        </h3>
                    </div>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800">
                        SEGURO
                    </span>
                </div>

                <div className="flex flex-col md:flex-row gap-2">
                    <div className="relative flex-grow group">
                        <input
                            readOnly
                            value={result}
                            className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 text-sm font-mono rounded-xl p-4 pr-12 focus:outline-none focus:ring-2 focus:ring-green-500/20 transition-all"
                        />
                        <a
                            href={result}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="absolute right-3 top-3.5 p-1 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-blue-500 transition-colors hidden md:block"
                        >
                            <ExternalLink className="w-4 h-4" />
                        </a>
                    </div>

                    <div className="flex gap-2 md:w-auto w-full">
                        {isMobile && (
                            <Button
                                type="button"
                                onClick={() => openLink(result)}
                                className="flex-1 md:hidden !bg-blue-600 !text-white !border-blue-600 h-auto"
                            >
                                <div className="flex items-center gap-2">
                                    <ExternalLink className="w-5 h-5" />
                                    <span>Abrir</span>
                                </div>
                            </Button>
                        )}

                        <Button
                            type="button"
                            onClick={handleCopy}
                            className={`flex-1 md:w-auto transition-all duration-300 h-auto ${copied
                                ? '!bg-green-500 !border-green-500 !shadow-none'
                                : '!bg-gray-900 dark:!bg-white !text-white dark:!text-gray-900'
                                }`}
                        >
                            {copied ? (
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 className="w-5 h-5" />
                                    <span>Copiado</span>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <Copy className="w-5 h-5" />
                                    <span>Copiar</span>
                                </div>
                            )}
                        </Button>
                    </div>
                </div>

                <div className="mt-4 flex items-center gap-4 text-xs text-gray-400 dark:text-gray-500">
                    <div className="flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> Verificado
                    </div>
                    <div className="flex items-center gap-1">
                        <TrendingDown className="w-3 h-3" /> Custo Competitivo
                    </div>
                </div>
            </div>
        </div>
    );
};
